// ALIGCUS SEO Schemas - JSON-LD Factory
// These schemas are injected at runtime via react-helmet-async (in addition to the
// pre-rendered ones in /public/index.html and /public/zh/index.html).
// Goal: when React hydrates the page, also inject FAQ + Breadcrumb + page-specific schemas.

const SITE = 'https://www.aligcus.com';
const ORG_ID = `${SITE}/#org`;

export function getOrganizationSchema(lang = 'en') {
  const isZh = lang === 'zh';
  return {
    '@context': 'https://schema.org',
    '@type': ['GeneralContractor', 'LocalBusiness'],
    '@id': ORG_ID,
    name: isZh ? 'ALIGCUS 阿里建筑' : 'ALIGCUS Construction Inc',
    alternateName: ['ALIGCUS', 'ALIGCUS 阿里建筑', '阿里建筑', 'ALIGCUS Construction'],
    url: isZh ? `${SITE}/zh/` : `${SITE}/`,
    logo: `${SITE}/api/uploads/f823eb4b3952.jpg`,
    image: `${SITE}/api/uploads/f823eb4b3952.jpg`,
    description: isZh
      ? '加州持照 ADU 后院屋专家。30+ 年经验，1500+ 项目落地。一站式设计·审批·建造·交付服务。'
      : "California's premier ADU builder with 30+ years of experience and 1500+ ADUs delivered. Full-service design, permit, build, and delivery.",
    foundingDate: '1995',
    telephone: ['+1-626-803-8000', '+1-408-900-6688'],
    email: 'office@aligcus.com',
    priceRange: '$$$',
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Los Angeles County' },
      { '@type': 'AdministrativeArea', name: 'Orange County' },
      { '@type': 'AdministrativeArea', name: 'Santa Clara County' },
      { '@type': 'AdministrativeArea', name: 'Alameda County' },
      { '@type': 'City', name: 'Los Angeles' },
      { '@type': 'City', name: 'Pasadena' },
      { '@type': 'City', name: 'Arcadia' },
      { '@type': 'City', name: 'San Gabriel' },
      { '@type': 'City', name: 'San Marino' },
      { '@type': 'City', name: 'Temple City' },
      { '@type': 'City', name: 'Diamond Bar' },
      { '@type': 'City', name: 'San Jose' },
      { '@type': 'City', name: 'Cupertino' },
      { '@type': 'City', name: 'Fremont' },
    ],
    address: [
      { '@type': 'PostalAddress', addressLocality: 'Los Angeles', addressRegion: 'CA', addressCountry: 'US' },
      { '@type': 'PostalAddress', addressLocality: 'San Jose', addressRegion: 'CA', addressCountry: 'US' },
    ],
    geo: { '@type': 'GeoCoordinates', latitude: '34.0522', longitude: '-118.2437' },
    knowsLanguage: ['en', 'zh-CN'],
    sameAs: ['https://www.youtube.com/@aligcus'],
  };
}

export function getWebSiteSchema(lang = 'en') {
  const isZh = lang === 'zh';
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': isZh ? `${SITE}/#website-zh` : `${SITE}/#website`,
    url: isZh ? `${SITE}/zh/` : `${SITE}/`,
    name: isZh ? 'ALIGCUS 阿里建筑' : 'ALIGCUS Construction',
    publisher: { '@id': ORG_ID },
    inLanguage: isZh ? 'zh-CN' : 'en-US',
  };
}

// Generate FAQ schema from i18n translations.
// Expects keys faq.q1...q10 and faq.a1...a10 to exist in i18n.
export function getFAQPageSchema(t) {
  const items = [];
  for (let i = 1; i <= 10; i++) {
    const q = t(`faq.q${i}`, { defaultValue: '' });
    const a = t(`faq.a${i}`, { defaultValue: '' });
    if (q && a && q !== `faq.q${i}` && a !== `faq.a${i}`) {
      items.push({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      });
    }
  }
  if (items.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items,
  };
}

// Generate Breadcrumb schema. items = [{name, url}]
export function getBreadcrumbSchema(items) {
  if (!items || items.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

// AggregateRating snippet (if you have real ratings to show)
export function getAggregateRatingSchema({ ratingValue = '5.0', reviewCount = 50 } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'AggregateRating',
    itemReviewed: { '@id': ORG_ID },
    ratingValue: String(ratingValue),
    reviewCount: String(reviewCount),
    bestRating: '5',
    worstRating: '1',
  };
}
